import { supabase } from '../lib/supabase';
import { logger, generateRequestId } from '../utils/logger';

/**
 * Real-time Audit Service
 * Handles real-time subscriptions to audit logs for instant notifications
 */

class AuditRealtimeService {
  constructor() {
    this.channel = null;
    this.listeners = [];
  }

  /**
   * Subscribe to audit log changes
   * @param {Function} callback - Callback function to handle real-time events
   * @param {Object} options - Subscription options
   * @returns {Object} Subscription channel
   */
  subscribe(callback, options = {}) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      // Create unique channel name
      const channelName = `audit_logs_${Date.now()}`;

      // Build filter if provided
      let filter = null;
      if (options?.userId) {
        filter = `user_id=eq.${options?.userId}`;
      } else if (options?.resourceType) {
        filter = `resource_type=eq.${options?.resourceType}`;
      } else if (options?.actionType) {
        filter = `action_type=eq.${options?.actionType}`;
      }

      // Create subscription configuration
      const subscriptionConfig = {
        event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
        schema: 'public',
        table: 'audit_logs'
      };

      if (filter) {
        subscriptionConfig.filter = filter;
      }

      // Create channel and subscribe
      this.channel = supabase?.channel(channelName)?.on(
          'postgres_changes',
          subscriptionConfig,
          (payload) => {
            logger?.info('Real-time audit event received', { 
              requestId, 
              event: payload?.eventType,
              resourceType: payload?.new?.resource_type,
              actionType: payload?.new?.action_type
            });

            // Transform payload to camelCase
            const transformedPayload = this.transformPayload(payload);

            // Call the callback with transformed data
            callback(transformedPayload);

            // Notify all registered listeners
            this.notifyListeners(transformedPayload);
          }
        )?.subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            logger?.info('Successfully subscribed to audit logs', { requestId, channelName });
          } else if (status === 'CHANNEL_ERROR') {
            logger?.error('Channel subscription error', { requestId, channelName });
          } else if (status === 'TIMED_OUT') {
            logger?.error('Channel subscription timed out', { requestId, channelName });
          }
        });

      return this.channel;
    } catch (error) {
      logger?.error('Failed to subscribe to audit logs', error, { requestId });
      throw error;
    }
  }

  /**
   * Subscribe to specific symptom data events
   * @param {Function} callback - Callback function
   * @returns {Object} Subscription channel
   */
  subscribeToSymptomEvents(callback) {
    return this.subscribe(callback, { resourceType: 'SYMPTOM_ENTRY' });
  }

  /**
   * Subscribe to export events
   * @param {Function} callback - Callback function
   * @returns {Object} Subscription channel
   */
  subscribeToExportEvents(callback) {
    return this.subscribe(callback, { actionType: 'EXPORT' });
  }

  /**
   * Subscribe to modification events (UPDATE, DELETE)
   * @param {Function} callback - Callback function
   * @returns {Object} Subscription channel
   */
  subscribeToModificationEvents(callback) {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      const channelName = `audit_modifications_${Date.now()}`;

      this.channel = supabase?.channel(channelName)?.on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'audit_logs'
          },
          (payload) => {
            // Filter for UPDATE and DELETE actions only
            const actionType = payload?.new?.action_type;
            if (actionType === 'UPDATE' || actionType === 'DELETE') {
              const transformedPayload = this.transformPayload(payload);
              callback(transformedPayload);
              this.notifyListeners(transformedPayload);
            }
          }
        )?.subscribe();

      return this.channel;
    } catch (error) {
      logger?.error('Failed to subscribe to modification events', error, { requestId });
      throw error;
    }
  }

  /**
   * Subscribe to access events (VIEW)
   * @param {Function} callback - Callback function
   * @returns {Object} Subscription channel
   */
  subscribeToAccessEvents(callback) {
    return this.subscribe(callback, { actionType: 'VIEW' });
  }

  /**
   * Register a listener for all audit events
   * @param {Function} listener - Listener function
   * @returns {Function} Unregister function
   */
  addListener(listener) {
    this.listeners?.push(listener);

    // Return unregister function
    return () => {
      this.listeners = this.listeners?.filter(l => l !== listener);
    };
  }

  /**
   * Notify all registered listeners
   * @param {Object} payload - Event payload
   */
  notifyListeners(payload) {
    this.listeners?.forEach(listener => {
      try {
        listener(payload);
      } catch (error) {
        logger?.error('Listener error', error);
      }
    });
  }

  /**
   * Transform payload from snake_case to camelCase
   * @param {Object} payload - Raw payload from Supabase
   * @returns {Object} Transformed payload
   */
  transformPayload(payload) {
    const record = payload?.new || payload?.old || {};

    return {
      eventType: payload?.eventType,
      timestamp: new Date()?.toISOString(),
      record: {
        id: record?.id,
        userId: record?.user_id,
        actionType: record?.action_type,
        resourceType: record?.resource_type,
        resourceId: record?.resource_id,
        changes: record?.changes,
        metadata: record?.metadata,
        ipAddress: record?.ip_address,
        userAgent: record?.user_agent,
        createdAt: record?.created_at
      },
      old: payload?.old ? {
        id: payload?.old?.id,
        userId: payload?.old?.user_id,
        actionType: payload?.old?.action_type,
        resourceType: payload?.old?.resource_type,
        resourceId: payload?.old?.resource_id,
        changes: payload?.old?.changes,
        metadata: payload?.old?.metadata,
        createdAt: payload?.old?.created_at
      } : null
    };
  }

  /**
   * Unsubscribe from audit log changes
   */
  unsubscribe() {
    const requestId = generateRequestId();
    logger?.setRequestId(requestId);

    try {
      if (this.channel) {
        supabase?.removeChannel(this.channel);
        logger?.info('Unsubscribed from audit logs', { requestId });
        this.channel = null;
      }

      // Clear all listeners
      this.listeners = [];
    } catch (error) {
      logger?.error('Failed to unsubscribe from audit logs', error, { requestId });
    }
  }

  /**
   * Get subscription status
   * @returns {boolean} True if subscribed
   */
  isSubscribed() {
    return this.channel !== null;
  }
}

// Export singleton instance
export const auditRealtimeService = new AuditRealtimeService();
export default auditRealtimeService;
